import { CREDIT_ROLE_LABELS } from "./media";
import Contributor from "../types/Contributor";
import Credit, { CreditRole } from "../types/Credit";
import Work from "../types/Work";

// Who a work is "by", as plain text: "Terry Pratchett, Neil Gaiman".
export const creatorNames = (work: Work): string =>
  (work.creators ?? []).map((c) => c.name).join(", ");

// A work's contributors for one role, in billing order.
export const contributorsFor = (
  credits: Credit[] | undefined,
  role: CreditRole,
): Contributor[] =>
  (credits ?? [])
    .filter((c) => c.role === role && c.contributor)
    .sort((a, b) => a.roleOrder - b.roleOrder)
    .map((c) => c.contributor!);

// A work's credits grouped by role, in display order; empty roles dropped.
export const groupByRole = (
  credits: Credit[] | undefined,
): { role: CreditRole; label: string; contributors: Contributor[] }[] =>
  CREDIT_ROLE_LABELS.map(([role, label]) => ({
    role,
    label,
    contributors: contributorsFor(credits, role),
  })).filter((group) => group.contributors.length > 0);

export type CreditedWork = { work: Work; roles: CreditRole[] };

// A contributor's credits as one entry per work, with every role they held
// on it.
export const creditedWorks = (
  credits: Credit[] | undefined,
): CreditedWork[] => {
  const out: CreditedWork[] = [];
  const byId = new Map<number, CreditedWork>();
  for (const credit of credits ?? []) {
    const work = credit.work;
    if (!work) continue;
    const entry = byId.get(work.id);
    if (!entry) {
      const created = { work, roles: [credit.role] };
      byId.set(work.id, created);
      out.push(created);
    } else if (!entry.roles.includes(credit.role)) entry.roles.push(credit.role);
  }
  return out;
};
